<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Progress Indicator -->
    <report-progress :showProgress="isLoading" />

    <!-- Table Container with Sticky Header -->
    <div class="flex-1 overflow-hidden">
      <div class="h-full flex flex-col">
        <!-- Single Table with Sticky Header -->
        <div class="flex-1 overflow-auto">
          <table class="w-full table-auto text-sm">
            <thead class="sticky top-0 z-10">
              <tr>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium py-2"
                  rowspan="3"
                ></th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  rowspan="3"
                >
                  {{ $t("reports.agent_kpi_report.head_day") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  rowspan="3"
                >
                  {{ $t("reports.agent_kpi_report.head_offered") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  colspan="2"
                  rowspan="2"
                >
                  {{ $t("reports.agent_kpi_report.head_answered") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  colspan="2"
                  rowspan="2"
                >
                  {{ $t("reports.agent_kpi_report.head_abandoned") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  rowspan="3"
                >
                  {{ $t("reports.agent_kpi_report.head_average_handle_time") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  rowspan="3"
                >
                  {{ $t("reports.agent_kpi_report.head_utilization") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  rowspan="3"
                >
                  {{ $t("reports.agent_kpi_report.head_occupancy") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  colspan="10"
                >
                  {{ $t("reports.agent_kpi_report.head_agent_behavior") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  colspan="4"
                >
                  {{ $t("reports.agent_kpi_report.head_fcr") }}
                </th>
              </tr>
              <tr>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  colspan="2"
                >
                  {{ $t("reports.agent_kpi_report.head_very_satisfied") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  colspan="2"
                >
                  {{ $t("reports.agent_kpi_report.head_satisfied") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  colspan="2"
                >
                  {{ $t("reports.agent_kpi_report.head_neutral") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  colspan="2"
                >
                  {{ $t("reports.agent_kpi_report.head_dissatisfied") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  colspan="2"
                >
                  {{ $t("reports.agent_kpi_report.head_very_dissatisfied") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  colspan="2"
                >
                  {{ $t("reports.agent_kpi_report.head_yes") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  colspan="2"
                >
                  {{ $t("reports.agent_kpi_report.head_no") }}
                </th>
              </tr>
              <tr>
                <th
                  v-for="index in 18"
                  :key="index"
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                >
                  {{ index % 2 !== 0 ? "#" : "%" }}
                </th>
              </tr>
            </thead>
            <tbody>
              <!-- Day rows (expandable) -->
              <template
                v-for="(dayData, dayIndex) in groupedByDay"
                :key="dayData.day"
              >
                <tr
                  :class="dayIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                  class="cursor-pointer hover:bg-gray-100"
                  @click="toggleDay(dayData.day)"
                >
                  <td
                    class="text-center text-primary-900 ps-2 py-2 border-b border-gray-200"
                  >
                    <v-icon size="small" color="primary-900">{{
                      expandedDays[dayData.day]
                        ? "mdi-minus-circle"
                        : "mdi-plus-circle-outline"
                    }}</v-icon>
                  </td>
                  <td
                    class="text-center text-teal pe-2 py-2 border-b border-gray-200"
                  >
                    <span class="font-weight-bold">{{ dayData.day }}</span>
                  </td>
                    <td
                      class="text-center text-blue-grey-lighten-1 px-2 py-2 border-b border-gray-200"
                    >
                      {{ dayData.totalCalls }}
                    </td>
                    <td
                      class="text-center text-cyan px-2 py-2 border-b border-gray-200"
                    >
                      {{ dayData.totalAnswered }}
                    </td>
                    <td
                      class="text-center text-cyan px-2 py-2 border-b border-gray-200"
                    >
                      {{ dayData.totalAnsweredPercentage }}%
                    </td>
                    <td
                      class="text-center text-blue-grey-lighten-1 px-2 py-2 border-b border-gray-200"
                    >
                      {{ dayData.totalAbandoned }}
                    </td>
                    <td
                      class="text-center text-blue-grey-lighten-1 px-2 py-2 border-b border-gray-200"
                    >
                      {{ dayData.totalAbandonedPercentage }}%
                    </td>
                    <td
                      class="text-center text-blue-grey-lighten-1 px-2 py-2 border-b border-gray-200"
                    >
                      {{ formatTime(dayData.avgHandleTime) }}
                    </td>
                    <td
                      class="text-center text-cyan px-2 py-2 border-b border-gray-200"
                    >
                      {{ dayData.avgUtilization ? dayData.avgUtilization.toFixed(2) + "%" : "-" }}
                    </td>
                    <td
                      class="text-center text-cyan px-2 py-2 border-b border-gray-200"
                    >
                      {{ dayData.avgOccupancy ? dayData.avgOccupancy.toFixed(2) + "%" : "-" }}
                    </td>
                    <!-- Agent behavior columns (10 columns) -->
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ dayData.ap_very_sat || 0 }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ dayData.ap_very_sat_rate || "0" }}%
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ dayData.ap_sat || 0 }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ dayData.ap_sat_rate || "0" }}%
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ dayData.ap_natural || 0 }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ dayData.ap_natural_rate || "0" }}%
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ dayData.ap_dissat || 0 }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ dayData.ap_dissat_rate || "0" }}%
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ dayData.ap_very_dissat || 0 }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ dayData.ap_very_dissat_rate || "0" }}%
                    </td>
                    <!-- FCR columns (4 columns) -->
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ dayData.fcr_yes || 0 }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ dayData.fcr_yes_rate || "0" }}%
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ dayData.fcr_no || 0 }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ dayData.fcr_no_rate || "0" }}%
                    </td>
                  </tr>
                  <!-- Agent rows (shown when day is expanded) -->
                  <tr
                    v-for="agent in dayData.agents"
                    v-show="expandedDays[dayData.day]"
                    :key="agent.agent || agent.id"
                    :class="
                      (dayIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white') + ' pl-8'
                    "
                  >
                    <td
                      class="text-center text-primary-900 ps-2 py-2 border-b border-gray-200"
                    ></td>
                    <td
                      class="text-center text-grey-darken-1 px-2 py-2 border-b border-gray-200"
                    >
                      {{ agent.name || agent.agent || "-" }}
                    </td>
                    <td
                      class="text-center text-blue-grey-lighten-1 px-2 py-2 border-b border-gray-200"
                    >
                      {{ agent.offered || 0 }}
                    </td>
                    <td
                      class="text-center text-cyan px-2 py-2 border-b border-gray-200"
                    >
                      {{ agent.answered || 0 }}
                    </td>
                    <td
                      class="text-center text-cyan px-2 py-2 border-b border-gray-200"
                    >
                      {{ agent.answer_rate || "0" }}%
                    </td>
                    <td
                      class="text-center text-blue-grey-lighten-1 px-2 py-2 border-b border-gray-200"
                    >
                      {{ agent.abandoned || 0 }}
                    </td>
                    <td
                      class="text-center text-blue-grey-lighten-1 px-2 py-2 border-b border-gray-200"
                    >
                      {{ agent.abandoned_rate || "0" }}%
                    </td>
                    <td
                      class="text-center text-blue-grey-lighten-1 px-2 py-2 border-b border-gray-200"
                    >
                      {{ formatTime(agent.aht) }}
                    </td>
                    <td
                      class="text-center text-cyan px-2 py-2 border-b border-gray-200"
                    >
                      {{ agent.utilization ? parseFloat(agent.utilization)?.toFixed(2) + "%" : "-" }}
                    </td>
                    <td
                      class="text-center text-cyan px-2 py-2 border-b border-gray-200"
                    >
                      {{ agent.occupancy ? parseFloat(agent.occupancy)?.toFixed(2) + "%" : "-" }}
                    </td>
                    <!-- Agent behavior columns (10 columns) -->
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ agent.ap_very_sat || 0 }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ agent.ap_very_sat_rate || "0" }}%
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ agent.ap_sat || 0 }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ agent.ap_sat_rate || "0" }}%
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ agent.ap_natural || 0 }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ agent.ap_natural_rate || "0" }}%
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ agent.ap_dissat || 0 }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ agent.ap_dissat_rate || "0" }}%
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ agent.ap_very_dissat || 0 }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ agent.ap_very_dissat_rate || "0" }}%
                    </td>
                    <!-- FCR columns (4 columns) -->
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ agent.fcr_yes || 0 }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ agent.fcr_yes_rate || "0" }}%
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ agent.fcr_no || 0 }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ agent.fcr_no_rate || "0" }}%
                    </td>
                  </tr>
                </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ReportProgress from "../../components/ReportProgress.vue";

export default {
  name: "kpi-report",
  components: {
    ReportProgress,
  },
  props: {
    selectedFilters: {
      type: Object,
      default: () => ({}),
    },
    skills: {
      type: Array,
      default: () => [],
    },
    reportData: {
      type: Array,
      default: () => [],
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    showCount: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expandedDays: {},
    };
  },
  methods: {
    ratingFormat(ratingScore) {
      if (ratingScore == "-" || isNaN(ratingScore)) {
        return { score: "-", color: "grey-darken-1" };
      }
      if (+ratingScore < 55) {
        return { score: ratingScore + "٪", color: "pink-accent-2" };
      }
      return { score: ratingScore + "٪", color: "teal-darken-1" };
    },
    extractDay(agent) {
      // Try to extract day from various possible fields
      if (agent.day) return agent.day;
      if (agent.date) return this.formatDay(agent.date);
      if (agent.calldate) return this.formatDay(agent.calldate);
      if (agent.period) return this.formatDay(agent.period);
      // If no date field, return a default
      return "Unknown";
    },
    formatDay(dateString) {
      if (!dateString) return "Unknown";
      // Try parsing as date
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        // Format as "YYYY-MM-DD" or "DD/MM/YYYY" based on locale
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
      }
      return dateString;
    },
    toggleDay(day) {
      // Use Vue 3 reactivity
      this.expandedDays = {
        ...this.expandedDays,
        [day]: !this.expandedDays[day],
      };
    },
    formatTime(seconds) {
      if (!seconds || seconds === 0 || isNaN(seconds)) {
        return "-";
      }
      const totalSeconds = Math.floor(parseFloat(seconds));
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;
      
      const pad = (num) => String(num).padStart(2, "0");
      return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
    },
  },
  computed: {
    currentLocal() {
      return this.$i18n.locale;
    },
    groupedByDay() {
      if (!this.reportData || this.reportData.length === 0) {
        return [];
      }

      // The API returns records with nested agents array
      // Each record represents a day with aggregated data and an agents array
      return this.reportData.map((dayRecord) => {
        const day = dayRecord.day || "-";
        const agents = dayRecord.agents || [];
        
        // Use day-level aggregated data for the summary row
        const offered = parseInt(dayRecord.offered) || 0;
        const answered = parseInt(dayRecord.answered) || 0;
        const abandoned = parseInt(dayRecord.abandoned) || 0;
        const answer_rate = parseFloat(dayRecord.answer_rate) || 0;
        const abandoned_rate = parseFloat(dayRecord.abandoned_rate) || 0;
        const aht = parseFloat(dayRecord.aht) || 0;
        const utilization = parseFloat(dayRecord.utilization) || 0;
        const occupancy = parseFloat(dayRecord.occupancy) || 0;
        
        return {
          day: day,
          agents: agents,
          totalCalls: offered,
          totalAnswered: answered,
          totalAbandoned: abandoned,
          totalAnsweredPercentage: answer_rate,
          totalAbandonedPercentage: abandoned_rate,
          avgHandleTime: aht,
          avgUtilization: utilization,
          avgOccupancy: occupancy,
          agentCount: agents.length,
          // Include day-level agent behavior and FCR data
          ap_very_sat: dayRecord.ap_very_sat || 0,
          ap_very_sat_rate: dayRecord.ap_very_sat_rate || "0",
          ap_sat: dayRecord.ap_sat || 0,
          ap_sat_rate: dayRecord.ap_sat_rate || "0",
          ap_natural: dayRecord.ap_natural || 0,
          ap_natural_rate: dayRecord.ap_natural_rate || "0",
          ap_dissat: dayRecord.ap_dissat || 0,
          ap_dissat_rate: dayRecord.ap_dissat_rate || "0",
          ap_very_dissat: dayRecord.ap_very_dissat || 0,
          ap_very_dissat_rate: dayRecord.ap_very_dissat_rate || "0",
          fcr_yes: dayRecord.fcr_yes || 0,
          fcr_yes_rate: dayRecord.fcr_yes_rate || "0",
          fcr_no: dayRecord.fcr_no || 0,
          fcr_no_rate: dayRecord.fcr_no_rate || "0",
        };
      }).sort((a, b) => {
        return a.day.localeCompare(b.day);
      });
    },
  },
};
</script>

<style scoped>
.set-border {
  border: 0.5px solid #d7eaf3;
}

.custom-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.03);
}

/* Optional hover effect */
.custom-striped tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.08) !important;
}
</style>
